/**
 * Groupe des tables (simples ou combinées) par espace pour les listes de l'équipe.
 * Par salle quand l'établissement en a, sinon « Intérieur » puis « Terrasse ».
 * Pure présentation : n'ajoute ni ne retire aucune table, conserve l'ordre reçu.
 */
export interface SpaceGroupableItem {
  isTerrace?: boolean;
  zoneId?: string | null;
}

export interface SpaceZone {
  id: string;
  name: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface TableSpaceGroup<T> {
  key: string;
  label: string;
  items: T[];
}

export function groupTablesBySpace<T extends SpaceGroupableItem>(
  items: T[],
  zones: SpaceZone[] = [],
): TableSpaceGroup<T>[] {
  const activeZones = [...zones]
    .filter(z => z.isActive !== false)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const useZones = activeZones.length > 0 && items.some(i => i.zoneId);

  if (useZones) {
    const groups: TableSpaceGroup<T>[] = activeZones.map(z => ({ key: z.id, label: z.name, items: [] }));
    const other: TableSpaceGroup<T> = { key: '__other', label: 'Sans salle', items: [] };
    items.forEach(item => {
      const g = groups.find(g => g.key === item.zoneId);
      (g ?? other).items.push(item);
    });
    return [...groups, other].filter(g => g.items.length > 0);
  }

  const indoor: TableSpaceGroup<T> = { key: 'indoor', label: 'Intérieur', items: [] };
  const terrace: TableSpaceGroup<T> = { key: 'terrace', label: 'Terrasse', items: [] };
  items.forEach(item => (item.isTerrace === true ? terrace : indoor).items.push(item));
  return [indoor, terrace].filter(g => g.items.length > 0);
}

/** True quand un regroupement apporte une information (plus d'un espace). */
export function hasMultipleSpaces<T extends SpaceGroupableItem>(items: T[], zones: SpaceZone[] = []): boolean {
  return groupTablesBySpace(items, zones).length > 1;
}
